import React, { useState, useRef, useEffect } from 'react';
import { 
  Send, 
  Loader2, 
  MessageSquare, 
  Sparkles, 
  AlertCircle,
  RefreshCw,
  BarChart3,
  X,
  Menu
} from 'lucide-react';
import api from '../../../shared/services/apiClient';
import ReactMarkdown from 'react-markdown';
import { toast } from 'react-hot-toast';
import { useUIStore } from '../../../shared/state/uiStore';
import { useDashboardJobStore } from '../../../shared/state/dashboardJobStore';
import { getRealtimeClient } from '../../../shared/services/realtimeClient';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../../../shared/services/queryKeys';

interface ChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
}

interface ConversationState {
  conversationId: string;
  currentIntent: string;
  validated: boolean;
  usedFacts: string[];
  usedDimensions: string[];
  assumptions: string[];
  warnings: string[];
  proposedArtifacts: any[];
  messages: ChatMessage[];
}

interface SuggestedAction {
  type: 'create_dashboard' | 'refine_analysis' | 'explore_metric';
  label: string;
  payload?: any;
}

const ChatAnalitico: React.FC = () => {
  const { sidebarOpen, setSidebarOpen } = useUIStore();
  const queryClient = useQueryClient();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [conversationState, setConversationState] = useState<ConversationState | null>(null);
  const [suggestedActions, setSuggestedActions] = useState<SuggestedAction[]>([]);
  const [error, setError] = useState<string | null>(null);
  const {
    jobId,
    status: jobStatus,
    events: jobEvents,
    widgets: jobWidgets,
    dashboardId,
    error: jobError,
    startJob,
    resetJob,
    applyProgress,
    applyWidget,
    markDone,
    markFailed,
  } = useDashboardJobStore();
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Auto-scroll al final
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Focus en input al cargar
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!jobId) return;
    const socket = getRealtimeClient();

    const handleProgress = (payload: any) => {
      applyProgress({ event: payload.event, status: payload.status, result: payload.result });
      const data = payload.event?.data;
      if (payload.event?.eventType === 'widget_ready' || payload.event?.stage?.includes('widget')) {
        if (data?.widgetId || data?.title) {
          applyWidget({ id: data?.widgetId, title: data?.title, type: data?.type });
        }
      }
    };

    const handleWidgetReady = (payload: any) => {
      handleProgress(payload);
    };

    const handlePlanReady = (payload: any) => {
      handleProgress(payload);
    };

    const handleJobDone = (payload: any) => {
      markDone({ result: payload.result });
      if (payload?.result?.dashboard?.id || dashboardId) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.dashboards });
        const targetId = payload?.result?.dashboard?.id || dashboardId;
        window.dispatchEvent(
          new CustomEvent('aion:navigate', {
            detail: { view: 'viewer', dashboardId: targetId },
          })
        );
      }
    };

    const handleJobFailed = (payload: any) => {
      markFailed({ error: payload?.error });
    };

    socket.emit('subscribe_job', { jobId });
    socket.on('job_started', handleProgress);
    socket.on('plan_ready', handlePlanReady);
    socket.on('widget_ready', handleWidgetReady);
    socket.on('job_progress', handleProgress);
    socket.on('job_done', handleJobDone);
    socket.on('job_failed', handleJobFailed);

    return () => {
      socket.emit('unsubscribe_job', { jobId });
      socket.off('job_started', handleProgress);
      socket.off('plan_ready', handlePlanReady);
      socket.off('widget_ready', handleWidgetReady);
      socket.off('job_progress', handleProgress);
      socket.off('job_done', handleJobDone);
      socket.off('job_failed', handleJobFailed);
    };
  }, [applyProgress, applyWidget, dashboardId, jobId, markDone, markFailed, queryClient]);

  const handleSendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setError(null);

    // Agregar mensaje del usuario a la UI
    const newUserMessage: ChatMessage = {
      role: 'user',
      content: userMessage,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, newUserMessage]);

    setLoading(true);

    try {
      const response = await api.post('/chat/message', {
        message: userMessage,
        conversationState
      });

      const { response: assistantResponse, conversationState: newState, suggestedActions: actions } = response.data;

      // Agregar respuesta del asistente
      const assistantMessage: ChatMessage = {
        role: 'assistant',
        content: assistantResponse,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, assistantMessage]);

      // Actualizar estado de conversación
      setConversationState(newState);

      // Actualizar acciones sugeridas
      if (actions && actions.length > 0) {
        setSuggestedActions(actions);
      } else {
        setSuggestedActions([]);
      }

    } catch (err: any) {
      console.error('Error sending message:', err);
      setError(err.response?.data?.message || 'Error al procesar el mensaje');
      
      // Agregar mensaje de error
      const errorMessage: ChatMessage = {
        role: 'assistant',
        content: '❌ Lo siento, hubo un error al procesar tu mensaje. Por favor intenta de nuevo.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleClearChat = () => {
    if (confirm('¿Estás seguro de que querés limpiar la conversación?')) {
      setMessages([]);
      setConversationState(null);
      setSuggestedActions([]);
      setError(null);
      inputRef.current?.focus();
    }
  };

  const handleAction = async (action: SuggestedAction) => {
    if (action.type === 'create_dashboard') {
      const dashboardName = prompt('Nombre del dashboard:');
      if (!dashboardName) return;

      try {
        setLoading(true);
        const response = await api.post('/chat/generate-dashboard', {
          conversationState,
          dashboardName
        });
        
        const result = response.data;
        if (result?.jobId) {
          startJob(result.jobId);
          toast.success('🚀 Generación iniciada, mostrando progreso en vivo...');
        } else {
          toast.success(`✅ ${result.message || 'Dashboard creado exitosamente'}`);
        }
        
      } catch (err: any) {
        console.error('Error creating dashboard:', err);
        toast.error('❌ Error al crear dashboard: ' + (err.response?.data?.message || err.message));
      } finally {
        setLoading(false);
      }
    }
  };

  const exampleQuestions = [
    '¿Qué puedo analizar con mis datos?',
    'Quiero ver cómo vendimos este mes',
    'Muéstrame un análisis de clientes',
    '¿Qué métricas tengo disponibles?'
  ];

  return (
    <div className="h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white flex flex-col">
      {/* Botón flotante para abrir sidebar cuando está cerrado */}
      {!sidebarOpen && setSidebarOpen && (
        <button
          onClick={() => setSidebarOpen(true)}
          className="fixed top-4 left-4 z-50 bg-slate-800 hover:bg-slate-700 text-white p-3 rounded-lg shadow-lg transition-colors border border-slate-700"
          title="Abrir menú"
        >
          <Menu className="w-5 h-5" />
        </button>
      )}
      
      {/* Messages Area - Con flex: 1 y min-height: 0 para scroll correcto */}
      <div className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-6">
        <div className="max-w-4xl mx-auto space-y-6">
          {jobId && (
            <div className="rounded-2xl border border-slate-700/70 bg-slate-900/70 p-4 sm:p-6">
              <div className="flex flex-col gap-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-sky-200">Generando dashboard</p>
                    <p className="text-xs text-slate-400">Job ID: {jobId}</p>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-slate-300">
                    <span className="inline-flex h-2 w-2 rounded-full bg-amber-400 animate-pulse" />
                    {jobStatus === 'DONE' ? 'Completado' : jobStatus === 'FAILED' ? 'Falló' : 'En progreso'}
                  </div>
                </div>
                {jobError && (
                  <div className="rounded-xl border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-200">
                    {jobError}
                  </div>
                )}
                <div className="space-y-2">
                  <p className="text-xs uppercase tracking-wide text-slate-500">Eventos recientes</p>
                  <ul className="space-y-1 text-sm text-slate-200">
                    {(jobEvents.length ? jobEvents.slice(-4) : [{ message: 'Esperando primeros eventos...' }]).map((event, idx) => (
                      <li key={`${event.at || 'event'}-${idx}`} className="flex items-center gap-2">
                        <span className="h-1.5 w-1.5 rounded-full bg-sky-400" />
                        {event.message}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="space-y-3">
                  <p className="text-xs uppercase tracking-wide text-slate-500">Widgets parciales</p>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {(jobWidgets.length ? jobWidgets : Array.from({ length: 2 }).map((_, index) => ({ id: `skeleton-${index}` }))).map((widget) => {
                      const widgetTitle = 'title' in widget ? widget.title : undefined;
                      return (
                      <div
                        key={widget.id}
                        className="rounded-xl border border-slate-700/60 bg-slate-900/80 p-3"
                      >
                        <div className="h-3 w-24 animate-pulse rounded bg-slate-700" />
                        <div className="mt-3 h-16 rounded-lg bg-slate-800/80">
                          <div className="h-full w-full animate-pulse rounded-lg bg-slate-700/60" />
                        </div>
                        <p className="mt-2 text-xs text-slate-400">
                          {widgetTitle || 'Preparando widget...'}
                        </p>
                      </div>
                      );
                    })}
                  </div>
                </div>
                {jobStatus === 'DONE' && (
                  <button
                    onClick={() => resetJob()}
                    className="self-start rounded-lg border border-slate-700 px-3 py-2 text-xs text-slate-300 hover:bg-slate-800"
                  >
                    Limpiar progreso
                  </button>
                )}
              </div>
            </div>
          )}
          {/* Welcome Message */}
          {messages.length === 0 && (
            <div className="flex items-center justify-center min-h-[60vh]">
              <div className="text-center max-w-2xl px-4">
                <div className="w-20 h-20 bg-slate-800 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <MessageSquare className="w-10 h-10 text-blue-500" />
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold mb-3">¡Hola! Soy tu analista de datos</h2>
                <p className="text-slate-400 mb-8 text-sm sm:text-base">
                  Podés preguntarme sobre tus datos en lenguaje natural. Voy a ayudarte a explorar, entender y crear análisis relevantes.
                </p>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl mx-auto">
                  {exampleQuestions.map((question, idx) => (
                    <button
                      key={idx}
                      onClick={() => setInput(question)}
                      className="bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-blue-500 rounded-xl p-4 text-left transition-all text-sm"
                    >
                      <p className="text-slate-300">{question}</p>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Messages */}
          {messages.map((message, idx) => (
            <div
              key={idx}
              className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[85%] sm:max-w-[80%] rounded-2xl p-3 sm:p-4 ${
                  message.role === 'user'
                    ? 'bg-gradient-to-br from-blue-500 to-blue-600 text-white'
                    : 'bg-slate-800/50 border border-slate-700'
                }`}
              >
                {message.role === 'assistant' ? (
                  <div className="prose prose-invert prose-sm max-w-none">
                    <ReactMarkdown>{message.content}</ReactMarkdown>
                  </div>
                ) : (
                  <p className="text-white text-sm sm:text-base">{message.content}</p>
                )}
                <p className="text-xs opacity-60 mt-2">
                  {new Date(message.timestamp).toLocaleTimeString('es-AR', { 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  })}
                </p>
              </div>
            </div>
          ))}

          {/* Loading */}
          {loading && (
            <div className="flex justify-start">
              <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-4">
                <div className="flex items-center gap-2">
                  <Loader2 className="w-5 h-5 animate-spin text-blue-400" />
                  <p className="text-slate-400">Pensando...</p>
                </div>
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-red-200">{error}</p>
              </div>
              <button
                onClick={() => setError(null)}
                className="text-red-400 hover:text-red-300"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Suggested Actions */}
          {suggestedActions.length > 0 && !loading && (
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4">
              <p className="text-sm text-yellow-200 mb-3 font-semibold">✨ Acciones sugeridas:</p>
              <div className="flex flex-wrap gap-2">
                {suggestedActions.map((action, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleAction(action)}
                    className="bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-300 px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                  >
                    <BarChart3 className="w-4 h-4" />
                    {action.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input Area - Fijo abajo */}
      <div className="flex-shrink-0 bg-slate-800/50 backdrop-blur border-t border-slate-700 p-4 sm:p-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3">
            <div className="flex-1 bg-slate-800 border border-slate-700 rounded-xl p-3 sm:p-4 flex items-center gap-3 focus-within:border-blue-500 transition-colors">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Preguntá sobre tus datos..."
                className="flex-1 bg-transparent border-none outline-none text-white placeholder-slate-500 resize-none text-sm sm:text-base"
                rows={1}
                style={{
                  minHeight: '24px',
                  maxHeight: '120px'
                }}
                disabled={loading}
              />
            </div>
            <button
              onClick={handleSendMessage}
              disabled={!input.trim() || loading}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-slate-700 disabled:cursor-not-allowed text-white p-3 rounded-xl transition-all flex items-center justify-center flex-shrink-0"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatAnalitico;
